import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import Button from "@mui/material/Button";
import SendIcon from "@mui/icons-material/Send";
import { KpiCard } from "@/components/common/KpiCard";
import { QueryState } from "@/components/common/AsyncState";
import { StatusChip } from "@/components/common/StatusChip";
import { fetchNotificationQueueStats, fetchNotifications, sendQueue } from "@/api/notifications";
import { NOTIFICATION_CHANNEL_LABELS, NOTIFICATION_STATUS_LABELS } from "@/types/enums";
import { formatDateTime, formatMoney, formatNumber } from "@/utils/format";

export function QueueTab() {
  const queryClient = useQueryClient();
  const { data: stats } = useQuery({ queryKey: ["notification-stats"], queryFn: fetchNotificationQueueStats });
  const { data, isLoading, error } = useQuery({
    queryKey: ["notifications"],
    queryFn: () => fetchNotifications({ limit: 100 }),
  });

  const sendMutation = useMutation({
    mutationFn: sendQueue,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["notifications"] });
      void queryClient.invalidateQueries({ queryKey: ["notification-stats"] });
    },
  });

  return (
    <>
      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} sm={3}>
          <KpiCard label="В очереди" value={formatNumber(stats?.queued ?? 0)} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <KpiCard label="Отправлено SMS" value={formatNumber(stats?.sentSms ?? 0)} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <KpiCard label="Отправлено Telegram" value={formatNumber(stats?.sentTelegram ?? 0)} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <KpiCard label="Баланс SMS-провайдера" value={formatMoney(stats?.smsBalance ?? 0)} />
        </Grid>
      </Grid>

      <Stack direction="row" justifyContent="flex-end" mb={2}>
        <Button variant="contained" startIcon={<SendIcon />} disabled={sendMutation.isPending} onClick={() => sendMutation.mutate()}>
          Отправить очередь
        </Button>
      </Stack>

      <QueryState isLoading={isLoading} error={error}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Дата</TableCell>
              <TableCell>Получатель</TableCell>
              <TableCell>Повод</TableCell>
              <TableCell>Канал</TableCell>
              <TableCell>Текст</TableCell>
              <TableCell>Статус</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(data?.items ?? []).map((n) => (
              <TableRow key={n.id}>
                <TableCell>{formatDateTime(n.sentAt ?? n.createdAt)}</TableCell>
                <TableCell>{n.recipientName ?? n.recipientPhoneOrChatId}</TableCell>
                <TableCell>{n.templateName ?? "Ручное сообщение"}</TableCell>
                <TableCell>{NOTIFICATION_CHANNEL_LABELS[n.channel]}</TableCell>
                <TableCell sx={{ maxWidth: 360 }}>{n.text}</TableCell>
                <TableCell>
                  <StatusChip label={NOTIFICATION_STATUS_LABELS[n.status]} />
                </TableCell>
              </TableRow>
            ))}
            {(data?.items ?? []).length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  Уведомлений пока нет.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </QueryState>
    </>
  );
}
